import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { signInWithGoogle, AuthActionError } from '@/lib/auth-actions';
import { POST_AUTH_REDIRECT_STORAGE_KEY } from '@/lib/safe-redirect';

import './GoogleSignInButton.css';

export function GoogleSignInButton() {
  const { t } = useTranslation(['auth']);
  const [submitting, setSubmitting] = useState(false);

  async function onClick() {
    setSubmitting(true);
    try {
      // Carry a post-login redirect (e.g. a deal page + resume action) across
      // the OAuth round-trip; AuthCallbackPage reads it back on return.
      const redirect = new URLSearchParams(window.location.search).get('redirect');
      if (redirect) sessionStorage.setItem(POST_AUTH_REDIRECT_STORAGE_KEY, redirect);
      else sessionStorage.removeItem(POST_AUTH_REDIRECT_STORAGE_KEY);
      await signInWithGoogle();
      // The SDK redirects to Google's consent screen via window.location;
      // in practice the promise above never resolves on the success path.
      // We leave `submitting` true so the button stays disabled until the
      // browser navigates away.
    } catch (error) {
      const code = error instanceof AuthActionError ? error.code : 'generic';
      if (code === 'rate_limited') {
        toast.error(t('auth:errors.rateLimited'));
      } else if (code === 'network') {
        toast.error(t('auth:errors.network'));
      } else if (code === 'oauth_account_not_linked') {
        toast.error(t('auth:errors.oauthAccountNotLinked'));
      } else if (code === 'provider_account_already_linked') {
        toast.error(t('auth:errors.providerAccountAlreadyLinked'));
      } else if (code === 'consent_denied') {
        toast.error(t('auth:errors.consentDenied'));
      } else {
        toast.error(t('auth:errors.generic'));
      }
      setSubmitting(false);
    }
  }

  return (
    <button type="button" className="google-signin" onClick={onClick} disabled={submitting}>
      <GoogleIcon />
      <span>{submitting ? t('auth:common.loading') : t('auth:google.label')}</span>
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
